import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import classes from './BorrowScreen.module.css';
import { doc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../config';

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M19 12H5M5 12L11 18M5 12L11 6" stroke="#2c3030" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ShareIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13" stroke="#2c3030" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M12 5V19M5 12H19" stroke="#2c3030" strokeWidth="2.5" strokeLinecap="round"/>
  </svg>
);

const MinusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M5 12H19" stroke="#2c3030" strokeWidth="2.5" strokeLinecap="round"/>
  </svg>
);

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const BorrowScreen = ({ group, onClose }) => {
  const user = auth.currentUser;
  const userName = group?.members?.find(m => m.id === user?.uid)?.name || user?.displayName || 'Someone';
  const festivalName = group?.festival?.name || 'the festival';
  const startDate = formatDate(group?.festival?.startdate);
  const endDate = formatDate(group?.festival?.enddate);
  const dateRange = startDate && endDate ? `${startDate} to ${endDate}` : startDate || endDate || '';

  const needItems = (group?.items || []).filter(item => {
    const qty = item?.needQuantity !== undefined
      ? item.needQuantity
      : (item?.organise || []).filter(o => o.method === 'Borrow' || o.method === 'Hire').length;
    return qty > 0;
  });

  const [qtys, setQtys] = useState(() => {
    const init = {};
    needItems.forEach(item => { init[item.id] = item.needQuantity || 1; });
    return init;
  });
  const [removedIds, setRemovedIds] = useState(new Set());
  const [removingIds, setRemovingIds] = useState(new Set());

  const [working, setWorking] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const updateQty = (id, delta) => {
    const current = qtys[id] || 1;
    if (delta === -1 && current === 1) {
      setRemovingIds(prev => new Set([...prev, id]));
      setTimeout(() => {
        setRemovedIds(prev => new Set([...prev, id]));
        setRemovingIds(prev => { const next = new Set(prev); next.delete(id); return next; });
      }, 350);
    } else {
      setQtys(prev => ({ ...prev, [id]: Math.max(1, current + delta) }));
    }
  };

  const doShare = async () => {
    if (working) return;
    setWorking(true);

    const shareUrl = `https://camping-buddy-site.web.app/share/group?id=${group.id}&type=borrow`;
    const shareText = `${userName} is going to ${festivalName}${dateRange ? ` from ${dateRange}` : ''} and needs to borrow some gear. Can you help?\n\n${shareUrl}`;

    // Share/copy MUST happen before any awaits — iOS revokes the user gesture
    // context the moment an unrelated promise is awaited.
    // Prefer the native share sheet (works on all mobile browsers without
    // clipboard permission), fall back to clipboard on desktop.
    let shared = false;
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText });
        shared = true;
      } catch (err) {
        if (err.name === 'AbortError') {
          // User dismissed the share sheet — treat as cancelled, don't proceed
          setWorking(false);
          return;
        }
        // Share failed for another reason — fall through to clipboard
      }
    }
    if (!shared) {
      try {
        await navigator.clipboard.writeText(shareText);
      } catch {
        fallbackCopy(shareText);
      }
    }

    // Update Firestore after sharing so the gesture context is never blocked
    try {
      const updatedItems = (group.items || []).map(item => {
        if (qtys[item.id] !== undefined && !removedIds.has(item.id)) {
          return { ...item, borrowStatus: 'requested', needQuantity: qtys[item.id] };
        }
        return item;
      });
      await updateDoc(doc(db, 'groups', group.id), { items: updatedItems });
    } catch (err) {
      console.log('Error updating borrow status:', err);
    }

    setWorking(false);
    setShowSuccess(true);
  };

  const fallbackCopy = (text) => {
    const el = document.createElement('textarea');
    el.value = text;
    el.style.cssText = 'position:fixed;top:0;left:0;opacity:0';
    document.body.appendChild(el);
    el.focus();
    el.select();
    try { document.execCommand('copy'); } catch (e) {}
    document.body.removeChild(el);
  };

  return (
    <div className={classes.container}>

      {/* Header */}
      <div className={classes.header}>
        <button className={classes.backBtn} onClick={onClose} aria-label="Back">
          <BackIcon />
        </button>
        <span className={classes.headerTitle}>Ask your mates</span>
        <button className={classes.shareBtn} onClick={doShare} aria-label="Share">
          <ShareIcon />
        </button>
      </div>

      <div className={classes.content}>

        {/* Items list */}
        <div className={classes.itemsCard}>
          {needItems.filter(item => !removedIds.has(item.id)).length === 0 ? (
            <p className={classes.emptyNote}>No items marked as needed yet.</p>
          ) : (
            needItems
              .filter(item => !removedIds.has(item.id))
              .map((item, i) => (
                <div
                  key={item.id}
                  className={`${classes.itemRow} ${i > 0 ? classes.itemRowBorder : ''} ${removingIds.has(item.id) ? classes.itemRowRemoving : ''}`}
                >
                  <span className={classes.itemName}>{item.name}</span>
                  <div className={classes.stepper}>
                    <button className={classes.stepBtn} onClick={() => updateQty(item.id, -1)}>
                      <MinusIcon />
                    </button>
                    <span className={classes.stepVal}>{qtys[item.id] || 1}</span>
                    <button className={classes.stepBtn} onClick={() => updateQty(item.id, 1)}>
                      <PlusIcon />
                    </button>
                  </div>
                </div>
              ))
          )}
        </div>

      </div>

      {/* Footer buttons */}
      <div className={classes.footer}>
        <button className={classes.cancelBtn} onClick={onClose}>Cancel</button>
        <button className={classes.copyBtn} onClick={doShare} disabled={working}>
          {working ? 'Copying...' : 'Copy link'}
        </button>
      </div>

      {/* Success bottom sheet */}
      {showSuccess && ReactDOM.createPortal(
        <>
          <div className={classes.backdrop} onClick={() => { setShowSuccess(false); onClose(); }} />
          <div className={classes.successSheet}>
            <span className={classes.successIcon}>🔗</span>
            <h2 className={classes.successTitle}>Share link created!</h2>
            <p className={classes.successMsg}>
              A link has been copied to the clipboard. You can paste this into a message to share this list with people.
            </p>
            <button className={classes.gotItBtn} onClick={() => { setShowSuccess(false); onClose(); }}>
              Got it!
            </button>
          </div>
        </>,
        document.body
      )}

    </div>
  );
};

export default BorrowScreen;
